import React, { Fragment, useState, useEffect } from 'react';
import {
  ChipField,
  Create,
  Edit,
  EditButton,
  email,
  Filter,
  maxLength,
  minLength,
  required,
  SaveButton,
  SearchInput,
  TextField,
  TextInput,
  Toolbar,
  SelectInput,
  useNotify,
  useRedirect,
} from 'react-admin';
import withStyles from '@material-ui/core/styles/withStyles';
import { ZippityDatagrid, ZippitySimpleForm } from '../../components';
import ImageLinkField from '../ImageLinkField';
import ImageUploadSelector from '../ImageUploadSelector';
import dataProvider from '../../dataProvider';
import './employee.css';
import BulkDeleteButton from '../../components/BulkDeleteButton';
import CustomTitle from '../../components/CustomTitle';
import NotificationPreferences from '../../components/NotificationPreferences';
import {
  getLocalStorage,
  getServiceEntity,
  getZ3PConfigValue,
  setLocalStorage,
} from '../../helper';
import ListWithCSVImport from '../../components/ListWithCSVImport';

const EmployeeFilter = (props) => (
  <Filter {...props}>
    <SearchInput source="q" alwaysOn />
  </Filter>
);

const listStyles = {
  nb_commands: { color: 'purple' },
};

const EmployeeBulkActionButtons = (props) => (
  <Fragment>
    <BulkDeleteButton {...props} />
  </Fragment>
);

const isUsingHelpText = getZ3PConfigValue('is_using_help_text');

const mapRoleNameToId = () => {
  return dataProvider
    .getList('role', {
      filter: {},
      sort: { field: 'role_id', order: 'ASC' },
      pagination: { page: 1, perPage: 100 },
    })
    .then((response) => {
      if (response?.data) {
        return response.data.reduce((map, role) => {
          // eslint-disable-next-line no-param-reassign
          map[role.name] = role.role_id;
          return map;
        }, {});
      }
      return {};
    });
};

export const EmployeeList = withStyles(listStyles)(({ classes, ...props }) => (
  <div>
    <ListWithCSVImport
      {...props}
      filters={<EmployeeFilter />}
      bulkActionButtons={<EmployeeBulkActionButtons />}
      sort={{ field: 'first_name', order: 'ASC' }}
      perPage={50}
    >
      <ZippityDatagrid>
        <TextField source="created_at" />
        <TextField source="modified_at" />
        <TextField source="first_name" sortable={false} />
        <TextField source="last_name" sortable={false} />
        <TextField source="email_address" />
        <ChipField source="slack_profile_id" className="selectableChip" />
        <ImageLinkField
          source="avatar_image_url"
          imgixParams="fit=facearea&facepad=1.6&mask=ellipse&w=60&h=60&auto=format"
          sortable={false}
        />
        {/* TODO: Display what service vehicles this employee is assigned to */}
        <EditButton />
      </ZippityDatagrid>
    </ListWithCSVImport>
    {isUsingHelpText && (
      <span>
        <h3>Steps to onboard a new technician:</h3>
        <ol>
          <li>
            <a href="https://zippitycars.slack.com/admin">Add to Slack</a>.
          </li>
          <li>
            If the technician will need a @zippitycars.com email address,{' '}
            <a href="https://admin.google.com/ac/users">add them to GSuite</a>.
          </li>
          <li>
            If the technician will use a personal Gmail address,{' '}
            <a href="https://manage.auth0.com/dashboard/us/zippity/rules">
              whitelist it for use in the Employee App
            </a>
            .
          </li>
          <li>
            <a href="https://admin.zippitycars.com/#/service-vehicle">
              Select their {getServiceEntity(false, 'lower')}
            </a>
            .
          </li>
        </ol>
      </span>
    )}
  </div>
));

const editStyles = {};

const validateString = [minLength(2), maxLength(63)];
const validateRequiredString = [required(), minLength(2), maxLength(63)];
const validateRequiredEmail = [required(), email()];

// Custom toolbar to add notification preferences to Edit and Create payloads
const EmployeeSaveToolbar = ({
  notificationTypesAndPreferences,
  pristine,
  ...rest
}) => {
  const getFormattedPreferenceList = () => {
    const formattedPreferenceList = [];
    Object.keys(notificationTypesAndPreferences).forEach((category) => {
      notificationTypesAndPreferences[category].forEach((eventType) => {
        formattedPreferenceList.push(eventType);
      });
    });
    return formattedPreferenceList;
  };

  return (
    <Toolbar {...rest}>
      <SaveButton
        submitOnEnter
        pristine={false}
        transform={(data) => ({
          ...data,
          notification_types_and_preferences: getFormattedPreferenceList(),
        })}
      />
    </Toolbar>
  );
};

export const EmployeeEdit = withStyles(editStyles)(({ classes, ...props }) => {
  const notify = useNotify();
  const redirect = useRedirect();

  const [
    notificationTypesAndPreferences,
    setNotificationTypesAndPreferences,
  ] = useState({});
  const [roleNameToId, setRoleNameToId] = useState({});

  useEffect(() => {
    mapRoleNameToId().then((map) => setRoleNameToId(map));
  }, []);
  const handleOnSuccess = ({ data }) => {
    const user = JSON.parse(getLocalStorage('user'));
    const employee = JSON.parse(getLocalStorage('employee'));
    if (user.id === employee.user_id) {
      setLocalStorage('employee', JSON.stringify(data));
    }
    notify(
      'ra.notification.updated',
      'info',
      { smart_count: 1 },
      props.undoable,
    );
    redirect('list', props.basePath, data.id, data);
  };
  return (
    <Edit
      undoable={false}
      title={
        <CustomTitle
          title="resources.employee.title"
          subTitle="resources.employee.subtitle"
        />
      }
      {...props}
      onSuccess={handleOnSuccess}
    >
      <ZippitySimpleForm
        toolbar={
          <EmployeeSaveToolbar
            notificationTypesAndPreferences={notificationTypesAndPreferences}
          />
        }
      >
        <TextField source="first_name" />
        <TextField source="last_name" />
        <TextInput validate={validateRequiredEmail} source="email_address" />
        <TextInput validate={validateString} source="slack_profile_id" />
        <ImageUploadSelector source="avatar_image_url" folder="technicians" />
        <div>
          <p>
            <strong>Full Admin:</strong> Access to Zippity Admin and the
            Technician app, including full messages
          </p>
          <p>
            <strong>Advanced Technician:</strong> Access to the Technician app
            with the ability to message day-of customers
          </p>
          <p>
            <strong>Basic Technician:</strong> Access to the Technician app only
          </p>
        </div>
        <SelectInput
          source="role_id"
          choices={[
            { id: roleNameToId.admin, name: 'Full Admin' },
            { id: roleNameToId.support, name: 'Advanced Technician' },
            { id: roleNameToId.technician, name: 'Basic Technician' },
          ]}
        />
        <NotificationPreferences
          notificationTypesAndPreferences={notificationTypesAndPreferences}
          setNotificationTypesAndPreferences={
            setNotificationTypesAndPreferences
          }
          employeeId={props.id}
        />
      </ZippitySimpleForm>
    </Edit>
  );
});

export const EmployeeCreate = withStyles(editStyles)(
  ({ classes, ...props }) => {
    const [
      notificationTypesAndPreferences,
      setNotificationTypesAndPreferences,
    ] = useState({});
    const [roleNameToId, setRoleNameToId] = useState({});

    useEffect(() => {
      mapRoleNameToId().then((map) => setRoleNameToId(map));
    }, []);

    return (
      <Create {...props}>
        <ZippitySimpleForm
          toolbar={
            <EmployeeSaveToolbar
              notificationTypesAndPreferences={notificationTypesAndPreferences}
            />
          }
        >
          <TextInput validate={validateRequiredString} source="first_name" />
          <TextInput validate={validateRequiredString} source="last_name" />
          <TextInput validate={validateRequiredEmail} source="email_address" />
          <TextInput validate={validateString} source="slack_profile_id" />
          <ImageUploadSelector source="avatar_image_url" folder="technicians" />
          <div>
            <p>
              <strong>Full Admin:</strong> Access to Zippity Admin and the
              Technician app, including full messages
            </p>
            <p>
              <strong>Advanced Technician:</strong> Access to the Technician app
              with the ability to message day-of customers
            </p>
            <p>
              <strong>Basic Technician:</strong> Access to the Technician app
              only
            </p>
          </div>
          <SelectInput
            source="role_id"
            choices={[
              { id: roleNameToId.admin, name: 'Full Admin' },
              { id: roleNameToId.support, name: 'Advanced Technician' },
              { id: roleNameToId.technician, name: 'Basic Technician' },
            ]}
          />
          <NotificationPreferences
            notificationTypesAndPreferences={notificationTypesAndPreferences}
            setNotificationTypesAndPreferences={
              setNotificationTypesAndPreferences
            }
          />
          <p>
            NOTE: When you click Save, the employee will receive a Welcome email
            and will be able to set a password.
          </p>
        </ZippitySimpleForm>
      </Create>
    );
  },
);
